<template>
  <div class="embed-code-modal">
    <div class="embed-header">
      <h3>{{ t('registration.embedCode') }}</h3>
      <button @click="$emit('close')" class="close-button">&times;</button>
    </div>

    <div class="embed-content">
      <div class="form-info">
        <h4>{{ registrationForm.title || registrationForm.name }}</h4>
        <p v-if="registrationForm.description" class="form-description">
          {{ registrationForm.description }}
        </p>
        <div class="form-stats">
          <span class="stat-item">
            <strong>{{ registrationForm.fields.length }}</strong>
            {{ t('registration.fields') }}
          </span>
          <span class="stat-item">
            <strong>{{ registrationForm.submissionCount || 0 }}</strong>
            {{ t('registration.submissions') }}
          </span>
        </div>
      </div>

      <div class="embed-options">
        <h4>{{ t('registration.embedOptions') }}</h4>

        <div class="option-group">
          <label class="option-label">
            <input
              type="radio"
              v-model="embedMethod"
              value="script"
              class="option-radio"
            />
            {{ t('registration.scriptEmbed') }}
          </label>
          <p class="option-description">
            {{ t('registration.scriptEmbedDesc') }}
          </p>
        </div>

        <div class="option-group">
          <label class="option-label">
            <input
              type="radio"
              v-model="embedMethod"
              value="container"
              class="option-radio"
            />
            {{ t('registration.containerEmbed') }}
          </label>
          <p class="option-description">
            {{ t('registration.containerEmbedDesc') }}
          </p>
        </div>

        <div v-if="embedMethod === 'container'" class="container-options">
          <label class="input-label">
            {{ t('registration.containerId') }}
            <input
              type="text"
              v-model="containerId"
              placeholder="my-registration-form"
              class="container-input"
            />
          </label>
        </div>
      </div>

      <div class="embed-code-section">
        <div class="code-header">
          <h4>{{ t('registration.embedCode') }}</h4>
          <button @click="copyEmbedCode" class="copy-button">
            <span class="copy-icon">📋</span>
            {{ t('general.copy') }}
          </button>
        </div>

        <div class="code-container">
          <pre class="code-block"><code>{{ generatedEmbedCode }}</code></pre>
        </div>
      </div>

      <div class="embed-instructions">
        <h4>{{ t('registration.howToUse') }}</h4>
        <div class="instruction-steps">
          <div class="step">
            <span class="step-number">1</span>
            <div class="step-content">
              <strong>{{ t('registration.copyCode') }}</strong>
              <p>
                {{ t('registration.copyCodeDesc') }}
              </p>
            </div>
          </div>

          <div class="step">
            <span class="step-number">2</span>
            <div class="step-content">
              <strong>{{ t('registration.pasteInWebsite') }}</strong>
              <p v-if="embedMethod === 'script'">
                {{ t('registration.pasteScriptDesc') }}
              </p>
              <p v-else>
                {{ t('registration.pasteContainerDesc') }}
              </p>
            </div>
          </div>

          <div class="step">
            <span class="step-number">3</span>
            <div class="step-content">
              <strong>{{ t('registration.testForm') }}</strong>
              <p>
                {{ t('registration.testFormDesc') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'RegistrationEmbedCode',
  props: {
    registrationForm: {
      type: Object,
      required: true,
    },
  },
  emits: ['close', 'copy-embed-code'],
  setup(props, { emit }) {
    const { t } = useI18n()

    const embedMethod = ref('script')
    const containerId = ref('my-registration-form')

    const baseUrl = computed(() => {
      return process.env.VUE_APP_BASE_URL
    })

    const generatedEmbedCode = computed(() => {
      const organizerId =
        props.registrationForm.organizerId ||
        props.registrationForm.organizer_id
      const formId = props.registrationForm.id

      if (embedMethod.value === 'script') {
        return `<!-- Registration Form Embed -->
<script src="${baseUrl.value}/registration.js"><\/script>
<script>
  initRegistrationForm('${organizerId}', '${formId}');
<\/script>`
      } else {
        return `<!-- Registration Form Container -->
<div id="${containerId.value}"></div>

<!-- Registration Form Script -->
<script src="${baseUrl.value}/registration.js"><\/script>
<script>
  embedRegistrationForm('${containerId.value}', '${organizerId}', '${formId}');
<\/script>`
      }
    })

    const copyEmbedCode = () => {
      navigator.clipboard
        .writeText(generatedEmbedCode.value)
        .then(() => {
          emit('copy-embed-code', generatedEmbedCode.value)
        })
        .catch((err) => {
          console.error('Failed to copy embed code:', err)
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = generatedEmbedCode.value
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          emit('copy-embed-code', generatedEmbedCode.value)
        })
    }

    return {
      t,
      embedMethod,
      containerId,
      generatedEmbedCode,
      copyEmbedCode,
    }
  },
}
</script>

<style scoped>
.embed-code-modal {
  width: 100%;
  max-width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.embed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #fff;
}

.embed-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #495057;
}

.embed-content {
  padding: 1rem 1.25rem;
  overflow-y: auto;
}

.form-info {
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.form-info h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.form-description {
  color: #6c757d;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.form-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  color: #495057;
  font-size: 0.9rem;
}

.embed-options {
  margin-bottom: 2rem;
}

.embed-options h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.option-group {
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  transition: border-color 0.2s ease;
}

.option-group:hover {
  border-color: #3498db;
}

.option-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.option-radio {
  margin-right: 0.75rem;
  transform: scale(1.2);
}

.option-description {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.container-options,
.competition-selection {
  margin: 1.5rem 0;
}

.input-label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.container-input,
.competition-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.container-input:focus,
.competition-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.15rem rgba(52, 152, 219, 0.2);
}

.field-help {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

.embed-code-section {
  margin-bottom: 2rem;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.code-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.copy-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
}

.copy-button:hover {
  background-color: #2980b9;
}

.copy-icon {
  font-size: 1rem;
}

.code-container {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

.code-block {
  margin: 0;
  padding: 0.85rem;
  background-color: #f8f9fa;
  color: #2c3e50;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
}

.embed-instructions {
  margin-bottom: 2rem;
}

.embed-instructions h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.instruction-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background-color: #3498db;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content strong {
  display: block;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.step-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.embed-features h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.feature-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.feature-list li {
  padding: 0.5rem 0;
  color: #495057;
  position: relative;
  padding-left: 1.5rem;
}

.feature-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #3498db;
  font-weight: bold;
}

/* Responsive Design */
@media (max-width: 768px) {
  .embed-code-modal {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .embed-content {
    padding: 1rem;
  }

  .form-stats {
    flex-direction: column;
    gap: 0.5rem;
  }

  .code-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .step {
    flex-direction: column;
    text-align: center;
  }

  .step-number {
    align-self: center;
  }
}
</style>
